import { emitter } from '@/utils/emitter'

export const ROOT_FONT_BASE_WIDTH = 1920
export const ROOT_FONT_BASE_SIZE = 75
export const ROOT_FONT_MIN_SIZE = 12

// rem 根字号的单一计算入口。
// rem.js 和业务里读取 html 字号的逻辑都共用这里，避免两处公式长期漂移。
export function getRootFontSize(winWidth = window.innerWidth) {
  const normalizedWidth = Number(winWidth)

  if (!Number.isFinite(normalizedWidth) || normalizedWidth <= 0) {
    return ROOT_FONT_BASE_SIZE
  }

  const responsiveSize = Math.round((normalizedWidth / ROOT_FONT_BASE_WIDTH) * ROOT_FONT_BASE_SIZE)

  return Math.max(ROOT_FONT_MIN_SIZE, Math.min(ROOT_FONT_BASE_SIZE, responsiveSize))
}

export function fontSize() {
  return getRootFontSize(window.innerWidth)
}

// 模块级别的变量，存储当前会话的 user_id
// 刷新页面时模块重新加载，变量会重新初始化，从而生成新的ID
let currentUserId = null

/**
 * 生成唯一的用户ID
 * 使用时间戳 + 随机数生成，确保标识符唯一
 * 在同一个页面会话期间（不刷新）返回相同的ID
 * 刷新页面或关闭重开浏览器时会生成新的ID
 * @returns {string} 唯一的用户ID
 */
export function generateUserId() {
  // 如果已有ID，直接返回（同一会话期间）
  if (currentUserId) {
    return currentUserId
  }
  // 生成新的ID：使用时间戳 + 随机数
  const timestamp = Date.now()
  const random = Math.floor(Math.random() * 1000000)
  currentUserId = `user_${timestamp}_${random}`
  return currentUserId
}

/**
 * 解析并持久化当前用户ID
 * 优先使用传入的 preferredUserId，否则自动生成
 * @param {string} preferredUserId - 优先使用的用户ID（来自路由参数等）
 * @returns {string} 最终使用的用户ID
 */
export function resolveCurrentUserId(preferredUserId = '') {
  const normalizedUserId = preferredUserId === null || preferredUserId === undefined
    ? ''
    : String(preferredUserId).trim()

  // 如果路由显式传入 userId，则以该值为当前页面会话内的唯一标识
  if (normalizedUserId) {
    currentUserId = normalizedUserId
  } else if (!currentUserId) {
    // 无显式 userId 时，仍保持“刷新页面后重新生成”的既有语义
    currentUserId = generateUserId()
  }

  // localStorage 只用于同步当前页已解析出的 userId，不作为刷新后的复用来源
  localStorage.setItem('mzw_user_id', currentUserId)
  return currentUserId
}

/**
 * 跨标签页事件通信工具
 * 使用 mitt emitter（当前标签页）+ localStorage storage 事件（其他标签页）实现通信
 */
const CROSS_TAB_EVENT_PREFIX = 'cross_tab_event_'

/**
 * 跨标签页触发事件
 * @param {string} eventName - 事件名称
 * @param {*} payload - 事件数据
 */
export function emitCrossTabEvent(eventName, payload) {
  const normalizedEventName = typeof eventName === 'string' ? eventName.trim() : ''
  if (!normalizedEventName) {
    console.warn('事件名称无效，无法发送跨标签页事件')
    return
  }

  // 1. 触发本地 mitt 事件（当前标签页）
  emitter.emit(normalizedEventName, payload)
  // 2. 写入 localStorage，触发其他标签页的 storage 事件
  const storageKey = `${CROSS_TAB_EVENT_PREFIX}${normalizedEventName}`
  const eventData = { eventName: normalizedEventName, data: payload, timestamp: Date.now(), random: Math.random() }
  try {
    localStorage.setItem(storageKey, JSON.stringify(eventData))
    // 立即删除，避免 localStorage 堆积
    setTimeout(() => localStorage.removeItem(storageKey), 100)
  } catch (error) {
    console.warn('跨标签页事件发送失败:', error)
  }
}

/**
 * 监听跨标签页事件
 * @param {string} eventName - 事件名称
 * @param {Function} handler - 处理函数
 * @returns {Function} 取消监听的函数
 */
export function onCrossTabEvent(eventName, handler) {
  const normalizedEventName = typeof eventName === 'string' ? eventName.trim() : ''
  if (!normalizedEventName) {
    console.warn('事件名称无效，无法监听跨标签页事件')
    return () => {}
  }

  if (typeof handler !== 'function') {
    console.warn('事件处理函数无效，无法监听跨标签页事件')
    return () => {}
  }

  // 1. 监听本地 mitt 事件（当前标签页）
  emitter.on(normalizedEventName, handler)
  // 2. 监听 storage 事件（来自其他标签页）
  const storageKey = `${CROSS_TAB_EVENT_PREFIX}${normalizedEventName}`
  const storageHandler = (event) => {
    // 只处理相关的事件
    if (event.key !== storageKey || !event.newValue) {
      return
    }

    try {
      const eventData = JSON.parse(event.newValue)
      // 跨标签页回调只触发当前订阅者，避免多个监听者时再次经 mitt 广播造成 N×N 放大。
      handler(eventData.data)
    } catch (error) {
      console.warn('处理跨标签页事件失败:', error)
    }
  }
  window.addEventListener('storage', storageHandler)
  // 返回取消监听的函数
  return () => {
    emitter.off(normalizedEventName, handler)
    window.removeEventListener('storage', storageHandler)
  }
}
